import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, MailCheck, MailX, Send, ServerCog, Info,
  AlertTriangle, ShieldCheck, RefreshCcw,
} from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { formatDate as formatTs } from "@/lib/format";

export default function SmtpSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);

  const [to, setTo] = useState(user?.email || "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([
        api.get("/settings/smtp/status"),
        api.get("/settings/smtp/audit?limit=20"),
      ]);
      setStatus(s.data);
      setAudit(a.data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSend = async () => {
    if (!to.trim()) {
      toast.error("Recipient email is required");
      return;
    }
    setSending(true);
    try {
      const payload = { to: to.trim() };
      if (subject.trim()) payload.subject = subject.trim();
      if (body.trim()) payload.body = body.trim();
      const { data } = await api.post("/settings/smtp/test", payload);
      if (data.sent) {
        toast.success(`Test email sent to ${payload.to}`);
      } else {
        toast.error(`Send failed: ${data.error || "unknown error"}`);
      }
      await load();
    } catch (err) {
      // Special-case 429 for friendlier UX.
      if (err?.response?.status === 429) {
        toast.error(err.response.data?.detail || "Rate limit exceeded");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSending(false);
    }
  };

  if (loading || !status) {
    return (
      <div className="p-6" data-testid="settings-smtp-loading">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const configuredBadge = status.configured ? (
    <StatusBadge tone="success" testId="settings-smtp-configured-badge">
      <MailCheck className="h-3 w-3" /> Configured
    </StatusBadge>
  ) : (
    <StatusBadge tone="danger" testId="settings-smtp-not-configured-badge">
      <MailX className="h-3 w-3" /> Not configured
    </StatusBadge>
  );

  return (
    <div className="p-6 space-y-6" data-testid="settings-smtp-page">
      <PageHeader
        testId="settings-smtp-header"
        title="SMTP"
        subtitle="Outgoing email configuration. Credentials live in the backend .env — this page is read-only."
        actions={
          <>
            <Button variant="outline" onClick={load} data-testid="settings-smtp-refresh-btn">
              <RefreshCcw className="h-4 w-4" /> Refresh
            </Button>
            <Button variant="outline" onClick={() => navigate("/settings")} data-testid="settings-smtp-back">
              Back to Settings
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status card */}
        <Card className="rounded-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ServerCog className="h-4 w-4 text-primary" /> Configuration
            </CardTitle>
            {configuredBadge}
          </CardHeader>
          <CardContent className="space-y-3">
            <KV label="Host" value={status.host} testId="settings-smtp-host" />
            <KV label="Port" value={status.port ?? null} testId="settings-smtp-port" />
            <KV label="User" value={status.user} testId="settings-smtp-user" />
            <KV label="From address" value={status.from_address} testId="settings-smtp-from-address" />
            <KV label="From name" value={status.from_name} testId="settings-smtp-from-name" />
            <KV label="TLS" value={status.use_tls === null ? null : (status.use_tls ? "Enabled" : "Disabled")} testId="settings-smtp-tls" />
            <KV label="Company name (settings)" value={status.company_name} testId="settings-smtp-company-name" />

            {!status.configured && status.missing_env?.length > 0 && (
              <div
                data-testid="settings-smtp-missing-env-notice"
                className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-warning" />
                <div>
                  <div className="font-medium">Missing required environment variables</div>
                  <div className="text-muted-foreground">
                    Set{" "}
                    {status.missing_env.map((v, i) => (
                      <span key={v} className="font-mono">
                        {i > 0 && ", "}
                        {v}
                      </span>
                    ))}
                    {" "}in <code>/app/backend/.env</code> and restart backend.
                  </div>
                </div>
              </div>
            )}

            <div className="pt-2 border-t border-border">
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                Last test send
              </div>
              {status.last_send_at ? (
                <div className="text-xs flex items-center gap-2" data-testid="settings-smtp-last-send">
                  <StatusBadge tone={status.last_send_status === "success" ? "success" : "danger"}>
                    {status.last_send_status}
                  </StatusBadge>
                  <span className="text-muted-foreground">{formatTs(status.last_send_at)}</span>
                  {status.last_send_error && (
                    <span className="text-destructive text-[10px] truncate max-w-xs" title={status.last_send_error}>
                      · {status.last_send_error}
                    </span>
                  )}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground italic" data-testid="settings-smtp-last-send-empty">
                  No test sends yet.
                </div>
              )}
            </div>

            {/* Batch V1.1 #22 — Recent PDF generations */}
            {status.recent_pdf_stats && (
              <div className="pt-2 border-t border-border" data-testid="settings-smtp-pdf-stats">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                  Recent PDF generations
                </div>
                {status.recent_pdf_stats.count > 0 ? (
                  <div className="text-xs grid grid-cols-4 gap-3">
                    <div>
                      <div className="text-muted-foreground text-[10px] uppercase tracking-wider">Count</div>
                      <div className="text-sm font-medium" data-testid="settings-smtp-pdf-stats-count">
                        {status.recent_pdf_stats.count}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-[10px] uppercase tracking-wider">Mean</div>
                      <div className="text-sm font-medium" data-testid="settings-smtp-pdf-stats-mean">
                        {status.recent_pdf_stats.mean_ms}ms
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-[10px] uppercase tracking-wider">p95</div>
                      <div className="text-sm font-medium" data-testid="settings-smtp-pdf-stats-p95">
                        {status.recent_pdf_stats.p95_ms}ms
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-[10px] uppercase tracking-wider">Max</div>
                      <div className="text-sm font-medium" data-testid="settings-smtp-pdf-stats-max">
                        {status.recent_pdf_stats.max_ms}ms
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground italic" data-testid="settings-smtp-pdf-stats-empty">
                    No PDFs generated since last backend restart.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Test send card */}
        <Card className="rounded-md">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Send className="h-4 w-4 text-primary" /> Send a test email
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Recipient <span className="text-destructive">*</span>
              </Label>
              <Input
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="you@example.com"
                data-testid="settings-smtp-test-to-input"
                disabled={!status.configured || sending}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Subject
              </Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={`SMTP test — ${status.company_name || "Field Service"}`}
                data-testid="settings-smtp-test-subject-input"
                disabled={!status.configured || sending}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Body
              </Label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="This is a test email from your Field Service app SMTP configuration."
                data-testid="settings-smtp-test-body-input"
                disabled={!status.configured || sending}
                className="w-full min-h-[100px] rounded-sm border border-border bg-background px-3 py-2 text-xs leading-relaxed focus:outline-none focus:border-primary disabled:bg-muted/50"
              />
            </div>

            <div className="flex items-start gap-2 rounded-md border border-info/40 bg-info/10 px-3 py-2 text-xs">
              <Info className="h-4 w-4 mt-0.5 shrink-0 text-info" />
              <div className="text-muted-foreground">
                A small <code>smtp-test.txt</code> attachment is included so the full attachment path is exercised.
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div
                className="text-xs text-muted-foreground"
                data-testid="settings-smtp-rate-limit"
              >
                <ShieldCheck className="h-3 w-3 inline mr-1" />
                {status.rate_limit.remaining}/{status.rate_limit.max} sends remaining this hour
              </div>
              <Button
                onClick={handleSend}
                disabled={!status.configured || sending || status.rate_limit.remaining === 0}
                data-testid="settings-smtp-send-btn"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                <span className="ml-1">Send test</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Audit log */}
      <Card className="rounded-md">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Recent test sends</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-xs" data-testid="settings-smtp-audit-table">
            <thead className="bg-muted/40 border-y border-border">
              <tr>
                <Th>When</Th>
                <Th>By</Th>
                <Th>To</Th>
                <Th>Subject</Th>
                <Th>Status</Th>
                <Th>Error</Th>
              </tr>
            </thead>
            <tbody>
              {auditLoading ? (
                <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">Loading…</td></tr>
              ) : audit.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-muted-foreground italic" data-testid="settings-smtp-audit-empty">
                    No test sends yet.
                  </td>
                </tr>
              ) : (
                audit.map((row) => (
                  <tr key={row.id} className="border-b border-border" data-testid={`settings-smtp-audit-row-${row.id}`}>
                    <td className="p-2 whitespace-nowrap">{formatTs(row.at)}</td>
                    <td className="p-2">{row.by_user_name}</td>
                    <td className="p-2 font-mono text-[11px]">{row.to}</td>
                    <td className="p-2 truncate max-w-[280px]">{row.subject}</td>
                    <td className="p-2">
                      <StatusBadge tone={row.status === "success" ? "success" : "danger"}>
                        {row.status}
                      </StatusBadge>
                    </td>
                    <td className="p-2 text-destructive text-[11px] truncate max-w-[220px]" title={row.error || ""}>
                      {row.error || "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function KV({ label, value, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-xs" data-testid={testId}>
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="font-mono truncate max-w-[60%]" title={value == null ? "" : String(value)}>
        {value == null || value === "" ? <span className="text-muted-foreground italic">unset</span> : String(value)}
      </span>
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="text-left p-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
      {children}
    </th>
  );
}
